import * as React from 'react';
import { useMemo } from "react";
import { DrillDownValuesAndDomains, RiskReportResults } from 'app/area/case-report/risk-listing/types-store';
import styles from './risk-matrix-listing.scss';
import { replaceTemplateString } from 'app/infrastructure/platform';
import {
  fillMissingAxis, getAxisDescription,
  mapToRiskMatrixPredefinedValues
} from 'app/area/risk-matrix-administration/mappers/map-to-risk-matrix-definition';
import { riskReportAttributeMappings } from 'app/area/reports/containers/chart/components/attributes/constants';
import { FavouriteDefinition } from 'app/types-shared';
import { AxisLabelMode, RiskMatrix } from 'app/components';
import { matrixRowHeight } from 'app/area/risk-matrix-administration/constants';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'app/types-store';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { FavouriteLayoutAttributes } from 'app/components/favourite-layout/types';
import {
  RiskReportLayoutAttributes, defaultMaxCaseNumbers,
  rowHeightForShowCaseNumbersEnabled
} from 'app/area/case-report/risk-listing/constants';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { ModalSize } from 'app/components/constants';

interface RiskMatricesProps {
  translation: any;
  riskReportResults: RiskReportResults;
  layoutAttributes: FavouriteLayoutAttributes;
  onDrilldown: (definition: FavouriteDefinition, drillDownValuesAndDomains: DrillDownValuesAndDomains) => void;
  workingDefinition: FavouriteDefinition;
  resultHasManyRiskMatrices: boolean;
  height?: number;
}

interface RiskMatricesState {
  caseNumbers: number[];
  modalTitle: string;
  totalCases: number;
}

export const RiskMatrices = ({
  translation,
  riskReportResults,
  layoutAttributes,
  onDrilldown,
  workingDefinition,
  height,
  resultHasManyRiskMatrices
}: RiskMatricesProps) => {
  const locale = useSelector((state: ApplicationState) => state?.app.locale);
  const [isOpen, setModalOpen] = React.useState(false);
  const [modalData, setModalData] = React.useState<RiskMatricesState>(undefined);
  const maxCaseNumbersInPopup = Number(layoutAttributes?.[RiskReportLayoutAttributes.MaxCaseNumbersInPopup]) || defaultMaxCaseNumbers;

  const renderModalContent = (caseNumbers: number[], totalCases: number) => {
    return <>
      <div><p>{totalCases} {translation.search_hits}</p></div>
      <div className={styles.caseNumberList}>
        {caseNumbers.slice(0, maxCaseNumbersInPopup).map(caseNumber => <a
          className={`${styles.caseNumberCell}`}
          href={`${toAbsoluteUrl('/case/' + caseNumber)}`}
          key={caseNumber}
        >
          {caseNumber}
        </a>)}
      </div>
    </>;
  };

  const riskMatrices = useMemo(() => {
    // Display message only when both attributes are false. Undefined is the default value processed in attributes mapping as true.
    const shouldDisplaySelectAttributesMessage = layoutAttributes?.[riskReportAttributeMappings.lossPotential] === false &&
      layoutAttributes?.[riskReportAttributeMappings.residualLossPotential] === false;

    if (shouldDisplaySelectAttributesMessage) {
      return <div className={`${styles.fullwidthContainer}`}>
        <div className="alert alert-warning">
          <p>{translation.warning_select_residual_or_loss_potential}</p>
        </div>
      </div>;
    }

    const rows: React.ReactNode[] = [];
    riskReportResults?.matrixDefs?.map((matrixDefinition, matrixDefinitionIndex) => {
      const isRiskMatrixTypeUnsupported = matrixDefinition.axes.length > 2;
      const riskMatrixUnsupportedMessage = `${replaceTemplateString(translation.risk_matrix_not_supported, matrixDefinition.type)}`;

      const columns = isRiskMatrixTypeUnsupported ?
        <div key={`alert-warning-${matrixDefinitionIndex}`} className="alert alert-warning">
          <p>{riskMatrixUnsupportedMessage}</p>
        </div>
        :
        riskReportResults?.results?.map(result => {
          const riskMatrixTypeResult = result?.riskMatrices.find(rm => rm.riskMatrixType === matrixDefinition.type);
          const onSelectedCell = (xAxisItemIndex: number, yAxisItemIndex: number) => {
            const drillDownData = riskMatrixTypeResult?.cells['x' + xAxisItemIndex + 'y' + yAxisItemIndex]?.data.drillDownValuesAndDomains;
            !!drillDownData && onDrilldown(workingDefinition, drillDownData);
          };
          const onCaseNumberModalOpen = (caseNumbers: number[], modalTitle: string, totalCases: number) => {
            setModalData({ caseNumbers, modalTitle, totalCases });
            setModalOpen(true);
          };
          const mappedRiskMatrixDefinition = { ...matrixDefinition, axes: fillMissingAxis(matrixDefinition.axes) };
          const textXAxisTitle = getAxisDescription(matrixDefinition?.axes[0].data.codetableId, translation);
          const textYAxisTitle = getAxisDescription(matrixDefinition?.axes[1]?.data.codetableId, translation);
          const isShowCaseNumbersEnabled = !!layoutAttributes[riskReportAttributeMappings.showCaseNumbers] &&
            !riskReportResults?.errorMessages?.length;

          return !riskMatrixTypeResult ? null :
            <div className={`${styles.riskMatrixReportCellWrapper}`} key={`${result.description}-${matrixDefinition.type}`}>
              <RiskMatrix
                className={styles.riskMatrixListing}
                componentId="risk_matrix"
                onSelectedXChanged={undefined}
                onSelectedYChanged={undefined}
                onSelectedCell={onSelectedCell}
                optionToCellValue={undefined}
                preselectedValues={mapToRiskMatrixPredefinedValues(undefined)}
                displayAllValues={true}
                readOnly={false}
                riskMatrixDefinition={mappedRiskMatrixDefinition}
                riskMatrixTypeResult={riskMatrixTypeResult}
                xAxisLabelMode={AxisLabelMode.all}
                yAxisLabelMode={AxisLabelMode.all}
                textXAxisTitle={textXAxisTitle}
                textYAxisTitle={textYAxisTitle}
                rowHeight={isShowCaseNumbersEnabled ? rowHeightForShowCaseNumbersEnabled : matrixRowHeight}
                locale={locale}
                title={`${riskReportResults.results.length > 1 ? result.description : ''}`}
                layoutAttributes={layoutAttributes}
                height={height}
                useSpacing={!resultHasManyRiskMatrices}
                onCaseNumberModalOpen={onCaseNumberModalOpen}
              />
            </div>
            ;
        });

      rows.push(<div className={`${styles.riskMatrixReportCell}`}
        key={`${matrixDefinition.type}-${matrixDefinitionIndex}`}>
        {riskReportResults?.matrixDefs?.length > 1 && <div className={`${styles.title}`}>
          <p className={`${styles.header}`}>{matrixDefinition.description}</p>
        </div>}
        {columns}
      </div>);
    }
    );
    return rows;
  }, [riskReportResults, onDrilldown]);

  const maxCaseNumbersPerCell = layoutAttributes?.[RiskReportLayoutAttributes.MaxCaseNumbersInPopup] || defaultMaxCaseNumbers;
  const caseNumberLimitMessage = modalData?.totalCases > maxCaseNumbersInPopup ? <p>
    {replaceTemplateString(translation.case_number_list_limited_to, maxCaseNumbersPerCell)}
  </p> : null;

  return <>
    {riskMatrices}
    {isOpen && <ModalWrapper
      onClose={() => setModalOpen(false)}
      onDone={() => setModalOpen(false)}
      headerTitle={modalData?.modalTitle}
      btnTitle={translation.done}
      size={ModalSize.large}
    >
      <div>
        {renderModalContent(modalData?.caseNumbers, modalData?.totalCases)}
        {caseNumberLimitMessage}
      </div>
    </ModalWrapper>}
  </>;
};

